import themeVariables from "@/config/theme/themeVariable";
import { Button, Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

interface ImplantsModalProps {
  forceOpened?: boolean;
  onClose?: () => void;
}

export const ImplantsModal = ({ forceOpened = false, onClose }: ImplantsModalProps) => {
  const [opened, { open, close }] = useDisclosure(forceOpened);
  const searchParams = useSearchParams();
  const [activeImplant, setActiveImplant] = useState<string | null>(null);
  
  useEffect(() => {
    if (searchParams?.get('modal') === 'implants' || forceOpened) {
      open();
      // 모달이 열릴 때 자동으로 첫번째 보형물 선택
      setActiveImplant('silicon');
    }
  }, [searchParams, open, forceOpened]);

  const implants = [
    {
      id: 'silicon',
      title: '실리콘 보형물',
      features: [
        '가장 일반적으로 사용되는 재료',
        '강도가 좋아 모양 유지에 효과적',
        '다양한 크기와 형태로 제작 가능',
        'L, I, C 등 다양한 형태 선택 가능'
      ],
      considerations: [
        '피부가 얇은 경우 비치거나 만져질 수 있음',
        '이물감이 있을 수 있음',
        '장기간 사용 시 이동 가능성',
        '제거 시 간단한 수술 필요'
      ]
    },
    {
      id: 'goretex',
      title: '고어텍스',
      features: [
        '미세다공성 구조로 조직 성장이 가능',
        '부드러운 촉감과 자연스러운 외관',
        '다양한 두께와 넓이로 사용 가능',
        '염증 반응이 적음'
      ],
      considerations: [
        '감염 발생 시 제거가 어려움',
        '구부러짐이나 변형 가능성',
        '실리콘보다 높은 비용',
        '모든 환자에게 적합하지 않을 수 있음'
      ]
    },
    {
      id: 'cartilage',
      title: '자가 연골',
      features: [
        '자신의 연골 조직을 사용해 이물질 반응이 없음',
        '이식 부위: 귀, 비중격, 늑연골 등',
        '가장 자연스러운 결과 기대 가능',
        '안정적인 형태 유지 가능'
      ],
      considerations: [
        '공여 부위의 추가 수술 필요',
        '수술 시간 증가 및 비용 상승',
        '사용 가능한 조직량에 제한이 있음',
        '형태 조정에 한계가 있을 수 있음'
      ]
    },
    {
      id: 'dermis',
      title: '자가 진피',
      features: [
        '자신의 피부 진피층 사용으로 이물질 반응 없음',
        '이식 부위: 측두근막, 대퇴부 등',
        '부드럽고 자연스러운 촉감',
        '다양한 부위에 적용 가능'
      ],
      considerations: [
        '공여 부위에 흉터가 남을 수 있음',
        '시간이 지나면서 일부 흡수될 가능성',
        '형태 유지력이 연골보다 약함',
        '장기적으로 안정성이 다소 떨어질 수 있음'
      ]
    }
  ];

  const handleItemClick = (id: string) => {
    setActiveImplant(id === activeImplant ? null : id);
  };

  const renderImplantContent = () => {
    if (!activeImplant) return null;
    
    const implant = implants.find(e => e.id === activeImplant);
    if (!implant) return null;
    
    return (
      <div className="mt-4 animate-fadeIn">
        <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100 text-center">{implant.title}</h2>
        
        {activeImplant === 'silicon' && (
          <>
            <div className="mb-6 flex justify-center">
              <img 
                src="https://ucarecdn.com/4e507b96-1638-4a97-80a9-1efc7f8fd6e8/Silicon_Implant.jpg" 
                alt="" 
                className="max-w-full rounded-lg shadow-sm border-1 border-gray-200 dark:border-gray-700"
                style={{ maxHeight: '300px' }}
              />
            </div>
            
            <div className="mb-6 flex justify-center">
              <img 
                src="https://ucarecdn.com/b3afc8ba-7f33-4ec4-a037-0e303708648a/noroot.png" 
                alt="" 
                className="max-w-full rounded-lg shadow-sm border-1 border-gray-200 dark:border-gray-700"
                style={{ maxHeight: '400px' }}
              />
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4 text-center">
                <span className="text-sm">가장 많이 사용하는 보형물로 원하는 모양으로 다듬기 쉽고, 수술이 간편합니다.</span>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-green-700 dark:text-green-400 mb-2 text-center">장점:</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
                    <li>모양 유지력이 우수하고 수술이 비교적 간단</li>
                    <li>비용이 합리적</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-red-700 dark:text-red-400 mb-2 text-center">단점:</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
                    <li>피부가 얇아지거나 비침 현상 발생 가능</li>
                    <li>감염이나 이물감이 발생할 수 있으며 장기적으로 캡슐 구축 가능성</li>
                  </ul>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-center">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">평균 비용</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">100~200만 원</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">자연스러움</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">중간</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">유지력</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">매우 높음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">감염위험</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">중간</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">이물감</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">중간</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">제거 가능성</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">쉬움</div>
                </div>
              </div>
            </div>
          </>
        )}
        
        {activeImplant === 'goretex' && (
          <>
            <div className="mb-6 flex justify-center">
              <img 
                src="https://ucarecdn.com/1a3a1c3f-7e3a-4909-95c1-4a9f3db72ea0/goretex.jpg" 
                alt="" 
                className="max-w-full rounded-lg shadow-sm border-1 border-gray-200 dark:border-gray-700"
                style={{ maxHeight: '300px' }}
              />
            </div>
            
            <div className="mb-6 flex justify-center">
              <img 
                src="https://ucarecdn.com/86793eb4-f55d-41e0-b6cb-91d39cac1cf1/noroot.png" 
                alt="" 
                className="max-w-full rounded-lg shadow-sm border-1 border-gray-200 dark:border-gray-700"
                style={{ maxHeight: '400px' }}
              />
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <span className="text-sm">미세한 구멍이 있어 주변 조직과 결합되어 안정성을 높이며 자연스러운 촉감을 제공합니다.</span>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-green-700 dark:text-green-400 mb-2 text-center">장점:</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
                    <li>자연스러운 촉감과 형태</li>
                    <li>이물감이 낮고 조직과 잘 융합됨</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-red-700 dark:text-red-400 mb-2 text-center">단점:</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
                    <li>제거 시 조직과 융합된 특성상 어려움</li>
                    <li>시간이 지나면 모양이 변형될 수 있음</li>
                  </ul>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-center">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">평균 비용</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">150~300만 원</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">자연스러움</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">매우 높음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">유지력</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">보통</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">감염위험</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">중간</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">이물감</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">적음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">제거 가능성</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">어려움</div>
                </div>
              </div>
            </div>
          </>
        )}
        
        {activeImplant === 'cartilage' && (
          <>
            <div className="mb-6 flex justify-center">
              <img 
                src="https://ucarecdn.com/2c6e9c3a-93c0-4b91-9232-31d8e1a4ac5a/cartilage.jpg" 
                alt="" 
                className="max-w-full rounded-lg shadow-sm border-1 border-gray-200 dark:border-gray-700"
                style={{ maxHeight: '300px' }}
              />
            </div>
            
            <div className="mb-6 flex justify-center">
              <img 
                src="https://ucarecdn.com/e9ffdcad-177e-4a3b-a1e4-f6182758e324/noroot.png" 
                alt="" 
                className="max-w-full rounded-lg shadow-sm border-1 border-gray-200 dark:border-gray-700"
                style={{ maxHeight: '400px' }}
              />
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <span className="text-sm">귀연골, 비중격연골, 늑연골 등 본인의 연골을 채취해 사용하는 방식으로 특히 코끝 성형이나 복잡한 재수술에 적합합니다.</span>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-green-700 dark:text-green-400 mb-2 text-center">장점:</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
                    <li>매우 자연스럽고 안전한 결과</li>
                    <li>이물반응이나 감염 위험이 거의 없음</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-red-700 dark:text-red-400 mb-2 text-center">단점:</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
                    <li>채취 부위에 흉터와 통증이 발생할 수 있음</li>
                    <li>일부 연골 흡수 및 변형 가능성</li>
                  </ul>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-center">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg col-span-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">평균 비용</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">귀연골 200~400만 원, 비중격연골 250~400만 원, 늑연골 500~800만 원</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">자연스러움</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">매우 높음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">유지력</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">보통</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">감염위험</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">매우 낮음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">이물감</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">거의 없음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">제거 가능성</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">어려움</div>
                </div>
              </div>
            </div>
          </>
        )}
        
        {activeImplant === 'dermis' && (
          <>
            <div className="mb-6 flex justify-center">
              <img 
                src="https://ucarecdn.com/28f3b3f6-1e3e-4e27-b52e-5cdb1e7d9786/dermis.jpg" 
                alt="" 
                className="max-w-full rounded-lg shadow-sm border-1 border-gray-200 dark:border-gray-700"
                style={{ maxHeight: '300px' }}
              />
            </div>
            
            <div className="mb-6 flex justify-center">
              <img 
                src="https://ucarecdn.com/4f58c1f4-3408-4fe2-b65c-c114e0bcd485/noroot.png" 
                alt="" 
                className="max-w-full rounded-lg shadow-sm border-1 border-gray-200 dark:border-gray-700"
                style={{ maxHeight: '400px' }}
              />
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <span className="text-sm">본인의 피부 진피층을 채취해 사용하는 방식으로 인체 친화적이고 매우 자연스럽습니다.</span>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-green-700 dark:text-green-400 mb-2 text-center">장점:</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
                    <li>면역 반응이 없어 안전함</li>
                    <li>매우 자연스러운 촉감과 외형</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-red-700 dark:text-red-400 mb-2 text-center">단점:</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
                    <li>일정량 흡수되어 원하는 높이가 유지되지 않을 수 있음</li>
                    <li>추가 절개가 필요하며 흉터 발생 가능</li>
                  </ul>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-center">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">평균 비용</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">200~350만 원</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">자연스러움</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">매우 높음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">유지력</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">낮음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">감염위험</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">낮음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">이물감</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">거의 없음</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">제거 가능성</div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">가능</div>
                </div>
              </div>
            </div>
          </>
        )}
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 dark:text-gray-300 mb-2">특징</h3>
            <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-400">
              {implant.features.map((feature, idx) => (
                <li key={idx}>{feature}</li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 dark:text-gray-300 mb-2">고려사항</h3>
            <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-400">
              {implant.considerations.map((consideration, idx) => (
                <li key={idx}>{consideration}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => {
          close();
          if (onClose) onClose();
        }}
        withCloseButton={false}
        centered
        radius={'lg'}
        padding={24}
        size='lg'
        closeOnClickOutside={true}
        classNames={{ 
        content: 'scrollbar-hide', 
        }}
      >
        <div className="flex flex-col gap-3 mb-5">
          <div className="text-lg text-gray-700 dark:text-gray-100 font-semibold" data-autofocus>
            보형물의 종류
          </div>
        </div>
        
        <div className="border-l-4 border-blue-400 dark:border-blue-600 pl-3 py-2 mb-4 bg-blue-50/50 dark:bg-blue-900/5">
          <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">💡 선택 팁</p>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <li><span className="font-medium">빠르고 확실한 결과</span>: <span className="text-blue-600 dark:text-blue-400">실리콘</span></li>
            <li><span className="font-medium">촉감과 자연스러움</span>: <span className="text-green-600 dark:text-green-400">고어텍스</span></li>
            <li><span className="font-medium">자연스러움과 안전성</span>: <span className="text-purple-600 dark:text-purple-400">자가진피</span></li>
            <li><span className="font-medium">코끝 성형과 재수술</span>: <span className="text-amber-600 dark:text-amber-400">자가연골</span></li>
          </ul>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 max-w-[560px] mx-auto">
          {implants.map((implant, index) => (
            <button
              key={index}
              className={`border border-gray-200 dark:border-gray-700 p-4 text-center h-[76px] text-gray-700 text-[15px] font-normal hover:border-blue-700 hover:text-blue-700 dark:hover:text-blue-400 transition-colors cursor-pointer rounded-lg bg-white dark:bg-gray-800 flex flex-col items-center justify-center ${activeImplant === implant.id ? 'border-blue-700 text-blue-700 dark:text-blue-400' : ''}`}
              onClick={() => handleItemClick(implant.id)}
            >
              <div className="text-[15px] font-normal">{implant.title}</div>
            </button>
          ))}
        </div>
        
        {activeImplant && renderImplantContent()}
        
        <div className="flex gap-1 justify-end mt-5">
          <Button 
            classNames={themeVariables.button.gray} 
            onClick={() => {
              close();
              if (onClose) onClose();
            }}
          >
            닫기
          </Button>
        </div>
      </Modal>
    </>
  );
}